import { normalizeText } from "./domain";

export type DuplicateReason = {
  code: "email" | "phone" | "name" | "organization" | "website" | "address";
  label: string;
};

export type ContactDuplicateInput = {
  id: string;
  name: string;
  email?: string | null;
  phone?: string | null;
  mobilePhone?: string | null;
  organizationIds?: string[];
};

export type SourceDuplicateInput = {
  id: string;
  name: string;
  website?: string | null;
  phone?: string | null;
  address?: string | null;
};

export type DuplicateScore = {
  confidence: number;
  reasons: DuplicateReason[];
};

export function normalizeEmail(value: unknown): string {
  return String(value ?? "").trim().toLowerCase();
}

export function normalizePhone(value: unknown): string {
  const digits = String(value ?? "").replace(/\D/g, "");
  if (digits.length > 10 && digits.startsWith("1")) return digits.slice(-10);
  return digits;
}

export function normalizeWebsite(value: unknown): string {
  const raw = String(value ?? "").trim().toLowerCase();
  if (!raw) return "";
  try {
    const url = new URL(/^https?:\/\//.test(raw) ? raw : `https://${raw}`);
    return url.hostname.replace(/^www\./, "");
  } catch {
    return raw.replace(/^https?:\/\//, "").replace(/^www\./, "").split(/[/?#]/)[0];
  }
}

function levenshtein(left: string, right: string): number {
  if (left === right) return 0;
  if (!left.length) return right.length;
  if (!right.length) return left.length;
  let previous = Array.from({ length: right.length + 1 }, (_, index) => index);
  for (let leftIndex = 1; leftIndex <= left.length; leftIndex += 1) {
    const current = [leftIndex];
    for (let rightIndex = 1; rightIndex <= right.length; rightIndex += 1) {
      current[rightIndex] = Math.min(
        current[rightIndex - 1] + 1,
        previous[rightIndex] + 1,
        previous[rightIndex - 1] + (left[leftIndex - 1] === right[rightIndex - 1] ? 0 : 1),
      );
    }
    previous = current;
  }
  return previous[right.length];
}

export function textSimilarity(leftValue: unknown, rightValue: unknown): number {
  const left = normalizeText(leftValue);
  const right = normalizeText(rightValue);
  if (!left || !right) return 0;
  if (left === right) return 1;
  return Math.max(0, 1 - levenshtein(left, right) / Math.max(left.length, right.length));
}

export function scoreContactDuplicate(
  left: ContactDuplicateInput,
  right: ContactDuplicateInput,
): DuplicateScore | null {
  if (left.id === right.id) return null;
  const reasons: DuplicateReason[] = [];
  const leftEmail = normalizeEmail(left.email);
  const rightEmail = normalizeEmail(right.email);
  const leftPhones = [left.phone, left.mobilePhone].map(normalizePhone).filter((phone) => phone.length >= 7);
  const rightPhones = new Set([right.phone, right.mobilePhone].map(normalizePhone).filter((phone) => phone.length >= 7));
  const nameSimilarity = textSimilarity(left.name, right.name);
  const organizations = new Set(left.organizationIds ?? []);
  const sharedOrganization = (right.organizationIds ?? []).some((id) => organizations.has(id));
  const exactEmail = Boolean(leftEmail && rightEmail && leftEmail === rightEmail);
  const exactPhone = leftPhones.some((phone) => rightPhones.has(phone));
  const exactName = nameSimilarity === 1;

  if (exactEmail) reasons.push({ code: "email", label: "Same email address" });
  if (exactPhone) reasons.push({ code: "phone", label: "Same phone number" });
  if (exactName) reasons.push({ code: "name", label: "Same full name" });
  else if (nameSimilarity >= 0.88) reasons.push({ code: "name", label: "Very similar name" });
  if (sharedOrganization) reasons.push({ code: "organization", label: "Connected to the same organization" });

  if (!exactEmail && !exactPhone && !(exactName && sharedOrganization) && !(nameSimilarity >= 0.88 && sharedOrganization)) {
    return null;
  }
  let confidence = 0;
  if (exactEmail) confidence += 0.55;
  if (exactPhone) confidence += 0.5;
  if (exactName) confidence += 0.28;
  else if (nameSimilarity >= 0.88) confidence += 0.2;
  if (sharedOrganization) confidence += 0.22;
  return { confidence: Math.min(0.99, Math.max(0.7, confidence)), reasons };
}

export function scoreSourceDuplicate(
  left: SourceDuplicateInput,
  right: SourceDuplicateInput,
): DuplicateScore | null {
  if (left.id === right.id) return null;
  const reasons: DuplicateReason[] = [];
  const nameSimilarity = textSimilarity(left.name, right.name);
  const leftWebsite = normalizeWebsite(left.website);
  const rightWebsite = normalizeWebsite(right.website);
  const leftPhone = normalizePhone(left.phone);
  const rightPhone = normalizePhone(right.phone);
  const addressSimilarity = textSimilarity(left.address, right.address);
  const exactName = nameSimilarity === 1;
  const sameWebsite = Boolean(leftWebsite && rightWebsite && leftWebsite === rightWebsite);
  const samePhone = Boolean(leftPhone.length >= 7 && leftPhone === rightPhone);
  const sameAddress = addressSimilarity >= 0.94;

  if (exactName) reasons.push({ code: "name", label: "Same organization name" });
  else if (nameSimilarity >= 0.84) reasons.push({ code: "name", label: "Very similar organization name" });
  if (sameWebsite) reasons.push({ code: "website", label: "Same website" });
  if (samePhone) reasons.push({ code: "phone", label: "Same phone number" });
  if (sameAddress) reasons.push({ code: "address", label: "Same address" });

  if (!exactName && !sameWebsite && !samePhone && !(nameSimilarity >= 0.84 && sameAddress)) return null;
  let confidence = 0;
  if (exactName) confidence += 0.7;
  else if (nameSimilarity >= 0.84) confidence += 0.48;
  if (sameWebsite) confidence += 0.35;
  if (samePhone) confidence += 0.32;
  if (sameAddress) confidence += 0.2;
  return { confidence: Math.min(0.99, Math.max(0.72, confidence)), reasons };
}
